// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { mergeWithCss } from "@sc/XDL/Utilities/mergeWithCss";
import React, { Children, FC, PropsWithChildren, useState } from "react";

export interface CartLineContainerProps {
    extendedStyles?: CartLineContainerStyles;
    includeActions?: boolean;
    [key: string]: any;
}
interface OwnProps {
    Image: FC<PropsWithChildren>;
    Description: FC<PropsWithChildren>;
    Price: FC<PropsWithChildren>;
    Qty: FC<PropsWithChildren>;
    Total: FC<PropsWithChildren>;
    Remove: FC<PropsWithChildren>;
    Actions: FC<PropsWithChildren>;
    Notes: FC<PropsWithChildren>;
}
export interface CartLineContainerStyles {
    container?: InjectableCss;
    image?: InjectableCss;
    description?: InjectableCss;
    price?: InjectableCss;
    qty?: InjectableCss;
    total?: InjectableCss;
    actions?: InjectableCss;
    notes?: InjectableCss;
    remove?: InjectableCss;
}

export const cartlineContainerStyles: CartLineContainerStyles = {};

const names = {
    Image: "Image",
    Description: "Description",
    Price: "Price",
    Qty: "Qty",
    Total: "Total",
    Remove: "Remove",
    Actions: "Actions",
    Notes: "Notes",
};

const Image: FC = () => null;
const Description: FC = () => null;
const Price: FC = () => null;
const Qty: FC = () => null;
const Total: FC = () => null;
const Remove: FC = () => null;
const Actions: FC = () => null;
const Notes: FC = () => null;

const CartLineContainer: React.FC<PropsWithChildren<CartLineContainerProps>> & OwnProps = ({
    children,
    extendedStyles,
    includeActions = true,
}) => {
    const [styles] = useState(() => mergeWithCss(cartlineContainerStyles, extendedStyles));

    const { Image, Description, Price, Qty, Total, Remove, Actions, Notes } = React.useMemo(() => {
        const childrenArray = Children.toArray(children) as React.ReactElement<PropsWithChildren>[];
        const MappedChilds = childrenArray.reduce(
            (results, current) => {
                const currentName = (current.type as React.NamedExoticComponent<any>)?.displayName;
                if (currentName) {
                    results[currentName] = current;
                }
                return results;
            },
            {} as Record<string, React.ReactElement<PropsWithChildren>>,
        );
        const slots = Object.entries(names).reduce(
            (results, [key, value], idx) => {
                results[value] = MappedChilds[value] || null;
                return results;
            },
            {} as Record<string, React.ReactElement<PropsWithChildren> | null>,
        );

        return slots;
    }, [children]);

    return (
        <StyledWrapper {...styles.container} {...{ includeActions }}>
            <StyledWrapper {...styles.image}>{Image?.props?.children}</StyledWrapper>
            <StyledWrapper {...styles.description}>{Description?.props?.children}</StyledWrapper>
            <StyledWrapper {...styles.price}>{Price?.props?.children}</StyledWrapper>
            <StyledWrapper {...styles.qty}>{Qty?.props?.children}</StyledWrapper>
            <StyledWrapper {...styles.total}>{Total?.props?.children}</StyledWrapper>
            {includeActions && <StyledWrapper {...styles.remove}>{Remove?.props?.children}</StyledWrapper>}
            <StyledWrapper {...styles.actions}>{Actions?.props?.children}</StyledWrapper>
            <StyledWrapper {...styles.notes} {...{ includeActions }}>
                {Notes?.props?.children}
            </StyledWrapper>
        </StyledWrapper>
    );
};

CartLineContainer.Image = Image;
CartLineContainer.Description = Description;
CartLineContainer.Price = Price;
CartLineContainer.Qty = Qty;
CartLineContainer.Total = Total;
CartLineContainer.Remove = Remove;
CartLineContainer.Actions = Actions;
CartLineContainer.Notes = Notes;

CartLineContainer.Image.displayName = names.Image;
CartLineContainer.Description.displayName = names.Description;
CartLineContainer.Price.displayName = names.Price;
CartLineContainer.Qty.displayName = names.Qty;
CartLineContainer.Total.displayName = names.Total;
CartLineContainer.Remove.displayName = names.Remove;
CartLineContainer.Actions.displayName = names.Actions;
CartLineContainer.Notes.displayName = names.Notes;

export default CartLineContainer;
